import React, { useState, useEffect, useContext } from "react";
import { useHistory, Link } from "react-router-dom";
import "./styles/TaskTopic.css";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Modal, Backdrop, Fade
} from "@material-ui/core";
import { useAlert } from "react-alert";
import axios from "axios";
import { createFilteredTask } from "../functions";
import { GlobalContext } from "../../context/GlobalState";
import UploadResponse from "../LearningResources/uploadResponse";
import { SubmissionPreviewModal } from "./SubmissionPreviewModal";
import { formatDate } from "../../utils/date";

const useStyles = makeStyles(() => ({
  tableContainer: {
    border: "2px solid black",
    borderRadius: "10px",
  },
  uploadBtn: {
    backgroundColor: "#5CE0D2",
    borderRadius: "27px",
    textTransform: "none",
    fontWeight: "bold",
  },
  viewBtn: {
    backgroundColor: "#4CAF50",
    color: "#fff",
    textTransform: "none",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBox: {
    background: "#fff",
    padding: "20px",
    borderRadius: "12px",
    width: "80%",
    maxWidth: "750px",
    outline: "none",
  },
  modalActions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "15px",
  },
}));

const TaskTopic = (props) => {
  const alert = useAlert();
  const history = useHistory();
  const classes = useStyles();

  const { grade, section, authToken } = useContext(GlobalContext).userDetails;

  const [task, setTask] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isDeadlinePassed = task && new Date(task.to) < new Date();

  const getRedirectLink = (task) => {
    const path = task.url
      ? `resource?url=${task.url}`
      : `modules/${task.content?._id}`;
    return `/tasklist/task/${task._id}/${path}`;
  };

  // ✅ FETCH TASK
  useEffect(() => {
    const getTask = async () => {
      try {
        let { taskid } = props.match.params;

        const res = await axios(
          `${process.env.REACT_APP_ORIGIN}/api/v1/grades/${grade}/sections/${section}/tasks/${taskid}`,
          {
            headers: { token: authToken },
          }
        );

        if (res.data.status === "success") {
          setTask(createFilteredTask(res.data.data.task));
          setSubmission(res.data.data.submission || null);
        }
      } catch (e) {
        alert.error("Task Not Found");
      }
    };

    getTask();
  }, [authToken, grade, section, props, alert]);

  // ✅ DELETE SUBMISSION
  const handleDelete = async () => {
    if (!submission?._id) return;

    if (!window.confirm("Are you sure you want to delete this submission?")) return;

    try {
      setDeleting(true);

      await axios.delete(
        `${process.env.REACT_APP_ORIGIN}/api/v1/grades/${grade}/sections/${section}/tasks/${task._id}/submittasks/${submission._id}`,
        {
          headers: { token: authToken },
        }
      );

      alert.success("Submission deleted successfully");
      setSubmission(null);
      setOpenModal(false);
    } catch (err) {
      alert.error("Failed to delete submission");
    } finally {
      setDeleting(false);
    }
  };

  document.title = "2xcell.in";

  return (
    <section className="taskTopic my-5">
      <div className="container">
        <div className="box mb-2">
          <h1 className="fw-bold">{task ? task.topic : "Topic"}</h1>
        </div>

        {!task ? (
          <h4>Loading...</h4>
        ) : (
          <>
            {/* ✅ TABLE */}
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Subject</TableCell>
                    <TableCell align="center">Chapter</TableCell>
                    <TableCell align="center">Topic</TableCell>
                    <TableCell align="center">Module</TableCell>
                    <TableCell align="center">Task Type</TableCell>
                    <TableCell align="center">Schedule</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell align="center">{task.subject}</TableCell>
                    <TableCell align="center">{task.chapter}</TableCell>
                    <TableCell align="center">{task.topic}</TableCell>

                    <TableCell align="center">
                      {task.onModel === "test" ? (
                        task.content?.name
                      ) : (
                        <Link
                          to={getRedirectLink(task)}
                          target="_blank"
                          style={{ color: "blue" }}
                        >
                          {task.url || task.content?.name}
                        </Link>
                      )}
                    </TableCell>

                    <TableCell align="center">{task.taskType}</TableCell>

                    {/* ✅ SCHEDULE */}
                    <TableCell align="center">
                      <div>
                        <div><strong>Start:</strong> {formatDate(task.from)}</div>
                        <div><strong>Due:</strong> {formatDate(task.to)}</div>
                      </div>
                    </TableCell>

                    {/* ✅ STATUS */}
                    <TableCell align="center">
                      {!submission ? (
                        <span style={{ color: "#999" }}>Not Submitted</span>
                      ) : submission.submitStatus === "submitted late" ? (
                        <span style={{ color: "orange", fontWeight: 600 }}>
                          Late Submission
                        </span>
                      ) : (
                        <span style={{ color: "green", fontWeight: 600 }}>
                          Submitted
                        </span>
                      )}
                    </TableCell>

                    {/* ✅ ACTION */}
                    <TableCell align="center">
                      {submission ? (
                        <Button
                          size="small"
                          className={classes.viewBtn}
                          onClick={() => setOpenModal(true)}
                        >
                          View / Manage
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          className={classes.uploadBtn}
                          onClick={() =>
                            history.push(
                              `/learningresources/modules-assesment/uploadresponse/${task._id}`
                            )
                          }
                        >
                          Upload
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* ✅ SHOW UPLOAD ONLY IF NO SUBMISSION */}
            {!submission && <UploadResponse />}

            <SubmissionPreviewModal
               task={task}
               submission={submission}
               onDelete={handleDelete}
               onClose={() => setOpenModal(false)}
               deleting={deleting}
               isDeadlinePassed={isDeadlinePassed}
               openModal={openModal}
               
            />
          </>
        )}
      </div>
    </section>
  );
};

export default TaskTopic;